import { useMutation, useQueryClient } from "@tanstack/react-query"
import { updateProfile, User } from "firebase/auth"
import createFirebaseApp from "firebase/firebase"
import {
  getStorage,
  ref as storageRef,
  uploadString,
  getDownloadURL,
} from "firebase/storage"
import { updateUserMutation } from "queries"
import useSessionStore from "stores/session"
import useSnackbarStore from "stores/snackbar"
import { SocialLinks } from "types/types"
import toBase64 from "./toBase64"

const app = createFirebaseApp()
const storage = getStorage(app)

interface Update {
  user?: User | null
  photoURL?: Blob
  name: string
  email?: string
  bio?: string
  links?: SocialLinks
}

export default function useUpdateUser() {
  const queryClient = useQueryClient()
  const toggleToast = useSnackbarStore((state) => state.toggleToast)
  const login = useSessionStore((state) => state.login)
  const userId = useSessionStore((state) => state.user?.uid)
  const updateUser = async ({
    user,
    photoURL,
    name,
    email,
    bio,
    links,
  }: Update) => {
    if (!user) throw new Error("user is not defined")
    const accessToken = await user?.getIdToken()
    const base64 = photoURL ? ((await toBase64(photoURL)) as string) : null

    const res = base64
      ? await fetch("/api/optimize-image", {
          method: "POST",
          body: base64,
        })
      : null
    const base64Image = res ? await res.json() : null

    const fileRef = photoURL
      ? storageRef(
          storage,
          `users/profile-picture_${user?.email}_${user?.uid}.webp`,
        )
      : null

    const uploadTask =
      fileRef && photoURL
        ? await uploadString(fileRef, base64Image, "base64", {
            contentType: "image/webp",
          })
        : null

    const imgURL = uploadTask
      ? await getDownloadURL(uploadTask.ref).then((url) => url)
      : null

    await updateProfile(user, {
      displayName: name,
      ...(imgURL && { photoURL: imgURL }),
    })

    const userData = {
      displayName: name,
      email: email,
      ...(imgURL && { photoURL: imgURL }),
      uid: user?.uid,
      bio: bio,
      accessToken,
      ...(links && { links: links }),
    }

    await updateUserMutation(userData)
    login({ ...userData, photoURL: imgURL ? imgURL : user.photoURL })
    return
  }

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] })
    },
    onError(error) {
      console.log(error)
      toggleToast({
        color: "yellow",
        text: "user data update failed",
      })
    },
  })

  return { updateUserMutation: mutateAsync, isPending }
}
